import "./DashboardPage.css";
import { BasePageComponent } from "../../../shared/components/pages/BasePageComponent";
import { GpsTrackerStatsResponse } from "../../../shared/response/hardware/GpsTrackerStatsResponse";
import { CampanaStatsResponse } from "../../../shared/response/marketing/CampanaResponse";
import { DashboardStatsResponse } from "../../../shared/response/proyectos/DashboardStatsResponse";
import { ClienteResponse } from "../../../shared/response/terceros/ClienteResponse";
import { FincaMapaResponse } from "../../../shared/response/terceros/FincaMapaResponse";
import { OrtomosaicoEstado } from "../../../shared/response/terceros/FincaResponse";
import { OrtomosaicoPendienteItem } from "../../../shared/response/terceros/OrtomosaicoPendienteItem";
import { AgendaResponse } from "../../../shared/response/ventas/AgendaResponse";
import { OportunidadResponse } from "../../../shared/response/ventas/OportunidadResponse";
import { PipelineStatsResponse } from "../../../shared/response/ventas/PipelineStatsResponse";
import { formatInt, formatKg } from "../../../shared/utils/formatUtils";
import { fmtCentavos } from "../../../shared/utils/proyectosFormat";
import { MenuPlataforma } from "../../permisosMenu";
import { GpsTrackerService } from "../../services/hardware/GpsTrackerService";
import { CampanaService } from "../../services/marketing/CampanaService";
import { ProyectosDashboardService } from "../../services/proyectos/ProyectosDashboardService";
import { ClienteService } from "../../services/terceros/ClienteService";
import { FincaMapaService } from "../../services/terceros/FincaMapaService";
import { OrtomosaicoWatchService } from "../../services/terceros/OrtomosaicoWatchService";
import { PipelineService } from "../../services/ventas/PipelineService";
import { PipelinePage, SC, SOURCE_LABELS, getStageLabel } from "../pipeline/PipelinePage";
import { ClientesPage, diasParaVencer, fmtVencimiento, planStatus } from "../terceros/ClientesPage";

type Pestana = "hoy" | "comercial" | "operacion";

const TITULO_PESTANA: Record<Pestana, string> = { hoy: "Hoy", comercial: "Comercial", operacion: "Operación" };

/** Desde qué uso del tope un cliente con plan entra en la lista «cerca del tope» (Sextante). */
const UMBRAL_TOPE = 0.9;

/**
 * Hectáreas de las licencias, sumadas por cliente con la lista de clientes (opción A, 2026-10-28: sin tocar el
 * servidor). Por eso una finca de dos clientes en demo cuenta dos veces en las de demo.
 */
interface Hectareas {
  /** Suma de los topes de los planes de producción vigentes. */
  contratadas: number;
  /** Hectáreas de las fincas de esos mismos clientes. */
  consumidas: number;
  /** Hectáreas de las fincas de los clientes con la demo vigente. */
  demoActivas: number;
  /** Hectáreas de las fincas de los clientes con la demo vencida. */
  demoInactivas: number;
  /** Clientes con plan vigente que usan el 90 % de su tope o más, del más lleno al menos lleno. */
  cercaDelTope: { cliente: ClienteResponse; uso: number }[];
  /**
   * Clientes con plan de producción vigente, tope 0 y fincas, del que más hectáreas tiene al que menos. Los crea así
   * la copia de datos del sistema anterior (`SextanteClienteCopyDataService` y `NitrofertClienteCopyDataService`).
   * Sus hectáreas cuentan en las consumidas pero no suman a las contratadas, y en la app del Cliente no les aparece
   * «Agregar finca».
   */
  sinTope: ClienteResponse[];
  /** Hectáreas de las fincas de los clientes `sinTope`. */
  haSinTope: number;
}

/**
 * Suma las hectáreas de cada licencia y separa los clientes cerca de su tope y los que tienen plan sin tope. Vigente y
 * vencida salen de `planStatus`, el mismo criterio de Clientes.
 */
function calcularHectareas(clientes: ClienteResponse[]): Hectareas {
  const h: Hectareas = {
    contratadas: 0, consumidas: 0, demoActivas: 0, demoInactivas: 0, cercaDelTope: [], sinTope: [], haSinTope: 0,
  };
  for (const c of clientes) {
    const estado = planStatus(c);
    if (estado === "sin-plan") continue;
    const vigente = estado !== "vencido";
    if (c.isDemoPlataforma) {
      if (vigente) h.demoActivas += c.hectareasTotales;
      else h.demoInactivas += c.hectareasTotales;
    } else if (vigente) {
      h.contratadas += c.maximoHectareasPlataforma;
      h.consumidas += c.hectareasTotales;
      if (c.maximoHectareasPlataforma > 0) {
        const uso = c.hectareasTotales / c.maximoHectareasPlataforma;
        if (uso >= UMBRAL_TOPE) h.cercaDelTope.push({ cliente: c, uso });
      } else if (c.cantFincas > 0) {
        h.sinTope.push(c);
        h.haSinTope += c.hectareasTotales;
      }
    }
  }
  h.cercaDelTope.sort((a, b) => b.uso - a.uso);
  h.sinTope.sort((a, b) => b.hectareasTotales - a.hectareasTotales);
  return h;
}

/** Fecha de vencimiento en ms, para ordenar. */
const msVencimiento = (c: ClienteResponse): number => new Date(c.fechaVencimientoPlataforma ?? 0).getTime();

/** Próximo contacto de una oportunidad en ms, para ordenar. */
const msContacto = (o: OportunidadResponse): number => new Date(o.proxContacto ?? 0).getTime();

/** Cuánto falta para el vencimiento, en palabras ("vence mañana", "venció hace 3 días"). */
function textoVence(dias: number): string {
  if (dias < 0) return `venció hace ${-dias} día${dias === -1 ? "" : "s"}`;
  if (dias === 0) return "vence hoy";
  if (dias === 1) return "vence mañana";
  return `vence en ${dias} días`;
}

/** Día y mes de una fecha ISO o Unix ms ("27 oct"). */
const fmtDia = (fecha: string | number): string => new Date(fecha).toLocaleDateString("es-CO", { day: "numeric", month: "short" });

/**
 * Dashboard de la Plataforma (2026-10-28): la pantalla con la que abre, con tres pestañas.
 * - **Hoy:** licencias (clientes con plan, demos, por vencer y vencidos), hectáreas, vencimientos de los próximos 30
 *   días con «Llamar», ortomosaicos en curso o con error, la agenda de hoy y la atrasada del Pipeline, y campañas.
 * - **Comercial:** indicadores, embudo, orígenes y comerciales del Pipeline, y las renovaciones del mes.
 * - **Operación:** fincas, ortomosaicos, GPS (solo Sextante) y proyectos.
 *
 * Cada recuadro aparece solo si el rol ve la opción del menú de la que salen sus datos (`calcularMenu`), y cada pestaña
 * pide sus datos la primera vez que se abre. Las cifras de clientes de un promotor cubren solo su cartera, como en
 * Clientes. Todo sale de consultas que ya existían: el Dashboard no tiene servicio propio en el Server.
 */
export class DashboardPage extends BasePageComponent {
  /** Pestañas ya cargadas: volver a una no repite sus consultas. */
  private readonly _cargadas = new Set<Pestana>();
  /** Lista de clientes que comparten Hoy y Comercial: se pide una sola vez por visita. */
  private _clientes: Promise<ClienteResponse[]> | null = null;
  /** Ortomosaicos pendientes que comparten Hoy y Operación: se piden una sola vez por visita. */
  private _ortomosaicos: Promise<OrtomosaicoPendienteItem[]> | null = null;

  /**
   * @param _menu Opciones del menú del usuario (`calcularMenu`): deciden qué pestañas y recuadros ve.
   * @param _promotorId Promotor del usuario si no es admin: las cifras de clientes y la agenda se acotan a lo suyo.
   * @param _isSextante Marca: cambia el recuadro de hectáreas.
   */
  constructor(
    private readonly _menu: MenuPlataforma,
    private readonly _promotorId: string | null,
    private readonly _isSextante: boolean,
  ) {
    super();
  }

  /** Ruta de la página. */
  public getAppRoute(): string { return "/dashboard"; }
  /** Título de la barra superior. */
  public getPageTitle(): string | null { return "Dashboard"; }

  /** Arma el encabezado y las pestañas que tienen algún recuadro para el rol, y abre la primera. */
  protected override renderPageContent(pageContent: HTMLDivElement): void {
    pageContent.className = "sx-page db-page";
    const pestanas = this._pestanas();
    const fecha = new Date().toLocaleDateString("es-CO", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    pageContent.innerHTML = `
      <div class="db-content">
        <div class="db-pg-hd">
          <div class="db-pg-ti">Dashboard</div>
          <div class="db-pg-su">${fecha}${this._promotorId ? " · tu cartera" : ""}</div>
        </div>
        ${pestanas.length === 0 ? `<div class="db-empty">No hay indicadores para tu rol.</div>` : `
        <div class="db-tabs" role="tablist">
          ${pestanas.map((p) => `<button class="db-tab" role="tab" data-pestana="${p}">${TITULO_PESTANA[p]}</button>`).join("")}
        </div>
        ${pestanas.map((p) => `<div class="db-view" id="db-view-${p}" role="tabpanel"></div>`).join("")}`}
      </div>
    `;
    pageContent.querySelectorAll<HTMLButtonElement>(".db-tab").forEach((tab) => {
      tab.onclick = () => this._mostrar(tab.dataset.pestana as Pestana);
    });
    if (pestanas.length > 0) this._mostrar(pestanas[0]);
  }

  /** Pestañas con al menos un recuadro para el rol, en orden. */
  private _pestanas(): Pestana[] {
    const m = this._menu;
    const pestanas: Pestana[] = [];
    if (m.showClientes || m.showCotizaciones || m.showMarketing) pestanas.push("hoy");
    if (m.showCotizaciones || m.showClientes) pestanas.push("comercial");
    if (m.showMapaFincas || m.showClientes || m.showHardware || m.showProyectos) pestanas.push("operacion");
    return pestanas;
  }

  /** Muestra la pestaña y, la primera vez, arma sus recuadros y pide sus datos. */
  private _mostrar(pestana: Pestana): void {
    this._pageContent.querySelectorAll<HTMLElement>(".db-tab").forEach((tab) => {
      const activa = tab.dataset.pestana === pestana;
      tab.classList.toggle("active", activa);
      tab.setAttribute("aria-selected", String(activa));
    });
    this._pageContent.querySelectorAll<HTMLElement>(".db-view").forEach((v) => {
      v.classList.toggle("active", v.id === `db-view-${pestana}`);
    });
    if (this._cargadas.has(pestana)) return;
    this._cargadas.add(pestana);
    const vista = this._pageContent.querySelector<HTMLElement>(`#db-view-${pestana}`)!;
    if (pestana === "hoy") this._armarHoy(vista);
    else if (pestana === "comercial") this._armarComercial(vista);
    else this._armarOperacion(vista);
  }

  // ── Pestañas ──

  /**
   * Hoy: licencias, hectáreas, las que vencen en 30 días y las ya vencidas (Clientes), ortomosaicos, agenda de hoy y
   * agenda atrasada (Pipeline; lado a lado en pantalla ancha) y campañas.
   */
  private _armarHoy(vista: HTMLElement): void {
    const m = this._menu;
    vista.innerHTML = `
      ${m.showClientes ? `<div class="db-kstrip" id="db-hoy-kpis"></div>` : ""}
      <div class="db-grid">
        ${m.showClientes ? this._panel("db-hoy-ha", "Hectáreas") : ""}
        ${m.showClientes ? this._panel("db-hoy-venc", "Licencias y demos que vencen en 30 días", { texto: "Ver clientes", ruta: "/terceros/clientes" }) : ""}
        ${m.showClientes ? this._panel("db-hoy-vencidas", "Licencias y demos vencidas", { texto: "Ver clientes", ruta: "/terceros/clientes" }) : ""}
        ${m.showClientes ? this._panel("db-hoy-orto", "Ortomosaicos") : ""}
        ${m.showCotizaciones ? this._panel("db-hoy-agenda", "Agenda de hoy", { texto: "Ver Pipeline", ruta: "/ventas/pipeline" }) : ""}
        ${m.showCotizaciones ? this._panel("db-hoy-atrasada", "Agenda atrasada", { texto: "Ver Pipeline", ruta: "/ventas/pipeline" }) : ""}
        ${m.showMarketing ? this._panel("db-hoy-camp", "Campañas", { texto: "Ver campañas", ruta: "/marketing/campanas" }) : ""}
      </div>
    `;
    this._conectarEnlaces(vista);
    if (m.showClientes) {
      const clientes = this._listaClientes();
      void this._cargar(this._el(vista, "db-hoy-kpis"), "indicadores de clientes", clientes, (l) => this._kpisClientesHtml(l));
      void this._cargar(this._el(vista, "db-hoy-ha"), "hectáreas", clientes,
        (l) => this._hectareasHtml(calcularHectareas(l)), (el, l) => this._conectarClientes(el, l));
      void this._cargar(this._el(vista, "db-hoy-venc"), "vencimientos", clientes,
        (l) => this._vencimientosHtml(l), (el, l) => this._conectarClientes(el, l));
      void this._cargar(this._el(vista, "db-hoy-vencidas"), "licencias vencidas", clientes,
        (l) => this._vencidasHtml(l), (el, l) => this._conectarClientes(el, l));
      void this._cargar(this._el(vista, "db-hoy-orto"), "ortomosaicos", this._listaOrtomosaicos(), (items) => this._ortomosaicosHtml(items));
    }
    if (m.showCotizaciones) {
      // Las dos agendas salen de la misma consulta.
      const agenda = this._agendaHoy();
      void this._cargar(this._el(vista, "db-hoy-agenda"), "agenda de hoy", agenda,
        (a) => this._agendaHtml(a), (el) => this._conectarAgenda(el));
      void this._cargar(this._el(vista, "db-hoy-atrasada"), "agenda atrasada", agenda,
        (a) => this._agendaAtrasadaHtml(a), (el) => this._conectarAgenda(el));
    }
    if (m.showMarketing) {
      void this._cargar(this._el(vista, "db-hoy-camp"), "campañas", CampanaService.instance.getStats(), (s) => this._campanasHtml(s));
    }
  }

  /** Comercial: indicadores, embudo, orígenes y comerciales del Pipeline, y las renovaciones de planes del mes. */
  private _armarComercial(vista: HTMLElement): void {
    const m = this._menu;
    const mes = new Date().toLocaleDateString("es-CO", { month: "long" });
    vista.innerHTML = `
      ${m.showCotizaciones ? `<div id="db-com-pipeline"></div>` : ""}
      ${m.showClientes ? `<div class="db-grid">${this._panel("db-com-renov", `Renovaciones de ${mes}`, { texto: "Ver más en el Pipeline", ruta: "/ventas/pipeline" })}</div>` : ""}
    `;
    this._conectarEnlaces(vista);
    if (m.showCotizaciones) {
      void this._cargar(this._el(vista, "db-com-pipeline"), "estadísticas del Pipeline", PipelineService.instance.getStats(),
        (s) => this._pipelineHtml(s), (el) => this._conectarEnlaces(el));
    }
    if (m.showClientes) {
      void this._cargar(this._el(vista, "db-com-renov"), "renovaciones del mes", this._listaClientes(),
        (l) => this._renovacionesHtml(l), (el, l) => this._conectarClientes(el, l));
    }
  }

  /** Operación: fincas (Mapa de fincas), ortomosaicos (Clientes), GPS (Hardware, solo Sextante) y proyectos. */
  private _armarOperacion(vista: HTMLElement): void {
    const m = this._menu;
    vista.innerHTML = `
      <div class="db-grid">
        ${m.showMapaFincas ? this._panel("db-op-fincas", "Fincas", { texto: "Ver mapa", ruta: "/terceros/mapa-fincas" }) : ""}
        ${m.showClientes ? this._panel("db-op-orto", "Ortomosaicos") : ""}
        ${m.showHardware ? this._panel("db-op-gps", "GPS", { texto: "Ver hardware", ruta: "/sistema/hardware" }) : ""}
        ${m.showProyectos ? this._panel("db-op-proy", "Proyectos", { texto: "Ver dashboard de proyectos", ruta: "/proyectos/dashboard" }) : ""}
      </div>
    `;
    this._conectarEnlaces(vista);
    if (m.showMapaFincas) {
      void this._cargar(this._el(vista, "db-op-fincas"), "fincas", FincaMapaService.instance.getAll(), (f) => this._fincasHtml(f));
    }
    if (m.showClientes) {
      void this._cargar(this._el(vista, "db-op-orto"), "ortomosaicos", this._listaOrtomosaicos(), (items) => this._ortomosaicosHtml(items));
    }
    if (m.showHardware) {
      void this._cargar(this._el(vista, "db-op-gps"), "GPS", GpsTrackerService.instance.getStats(), (s) => this._gpsHtml(s));
    }
    if (m.showProyectos) {
      void this._cargar(this._el(vista, "db-op-proy"), "proyectos", ProyectosDashboardService.instance.getStats(), (s) => this._proyectosHtml(s));
    }
  }

  // ── Datos ──

  /**
   * Clientes del usuario: la lista completa para un admin, su cartera para un promotor (mismo filtro que Clientes).
   * Se pide una vez por visita. Usa `sync()` y no `get()`: `get()` nunca rechaza y, si falla, devuelve lo último
   * cargado, que al entrar es una lista vacía; los recuadros mostrarían ceros como si fueran datos reales (revisión
   * del 2026-10-29). Con `sync()` la falla llega a `_cargar` y cada recuadro dice «No se pudo cargar.».
   */
  private _listaClientes(): Promise<ClienteResponse[]> {
    this._clientes ??= ClienteService.instance.sync().then(() => {
      const lista = ClienteService.instance.items;
      return this._promotorId ? lista.filter((c) => c.promotorId === this._promotorId) : lista;
    });
    return this._clientes;
  }

  /** Publicaciones de ortomosaico en curso y con error; se piden una vez por visita. */
  private _listaOrtomosaicos(): Promise<OrtomosaicoPendienteItem[]> {
    this._ortomosaicos ??= OrtomosaicoWatchService.instance.listarPendientes();
    return this._ortomosaicos;
  }

  /**
   * Agenda del Pipeline para los recuadros «Agenda de hoy» (de las 0:00 a las 23:59 del navegador) y «Agenda atrasada»
   * (los vencidos); la de un promotor, solo con lo suyo. Es una sola consulta para los dos.
   * De los vencidos se quedan solo los de antes de hoy: el Server los calcula con la fecha UTC, que desde las 7:00 p. m.
   * de Colombia ya es la de mañana, y sin este filtro las demos y seguimientos de hoy salían también como vencidos
   * (revisión del 2026-10-29).
   */
  private async _agendaHoy(): Promise<AgendaResponse> {
    const ahora = new Date();
    const desde = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate()).toISOString();
    const hasta = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate(), 23, 59, 59).toISOString();
    const agenda = await PipelineService.instance.getAgenda(desde, hasta);
    const id = this._promotorId;
    const propias = (lista: OportunidadResponse[]): OportunidadResponse[] => (id ? lista.filter((o) => o.comercialId === id) : lista);
    const inicioDeHoy = Date.parse(desde);
    const vencidos = propias(agenda.vencidos).filter((o) => o.proxContacto !== null && Date.parse(o.proxContacto) < inicioDeHoy);
    return { demos: propias(agenda.demos), pendientes: propias(agenda.pendientes), vencidos };
  }

  /**
   * Pinta un recuadro con «Cargando…», espera sus datos y lo rellena. Si algo falla, lo dice en el recuadro y escribe
   * el error completo en consola; los demás recuadros siguen. Si el usuario ya salió de la página, no pinta nada.
   */
  private async _cargar<T>(
    el: HTMLElement,
    nombre: string,
    datos: Promise<T>,
    pintar: (d: T) => string,
    conectar?: (el: HTMLElement, d: T) => void,
  ): Promise<void> {
    el.innerHTML = `<div class="db-empty">Cargando…</div>`;
    try {
      const d = await datos;
      if (!el.isConnected) return;
      el.innerHTML = pintar(d);
      conectar?.(el, d);
    } catch (error) {
      console.warn(`[DashboardPage._cargar] no se pudo cargar el recuadro «${nombre}»`, error);
      if (el.isConnected) el.innerHTML = `<div class="db-error">No se pudo cargar.</div>`;
    }
  }

  // ── Recuadros ──

  /** Indicadores de licencias: clientes con plan vigente, demos vigentes, por vencer en 30 días y vencidos. */
  private _kpisClientesHtml(clientes: ClienteResponse[]): string {
    let planes = 0, demos = 0, porVencer = 0, vencidos = 0;
    for (const c of clientes) {
      const estado = planStatus(c);
      if (estado === "sin-plan") continue;
      if (estado === "vencido") { vencidos++; continue; }
      if (estado === "por-vencer") porVencer++;
      if (c.isDemoPlataforma) demos++;
      else planes++;
    }
    return [
      this._kpi(formatInt(planes), "Clientes activos", "green", "con plan de producción vigente"),
      this._kpi(formatInt(demos), "Demos activas", "blue", "demo vigente"),
      this._kpi(formatInt(porVencer), "Por vencer", "amber", "demos y planes, en 30 días"),
      this._kpi(formatInt(vencidos), "Vencidos", "red", "demos y planes"),
    ].join("");
  }

  /**
   * Hectáreas. Sextante: consumidas contra contratadas de los planes vigentes, disponibles o excedidas, uso, las de
   * las demos aparte, y la lista de los clientes con plan sin tope asignado y de los que están en el 90 % de su tope o
   * más. Nitrofert: contratadas más las de demos activas; aparte, las de demos activas y vencidas (las vencidas no
   * suman al total) y los planes sin tope asignado, que tampoco suman.
   */
  private _hectareasHtml(h: Hectareas): string {
    if (!this._isSextante) {
      const total = h.contratadas + h.demoActivas;
      const clientesSinTope = `${formatInt(h.sinTope.length)} cliente${h.sinTope.length === 1 ? "" : "s"}`;
      return `
        <div class="db-ha-big">${formatKg(total)} ha</div>
        <div class="db-ha-sub">${formatKg(h.contratadas)} contratadas + ${formatKg(h.demoActivas)} en demo = ${formatKg(total)} ha</div>
        <div class="db-cifras">
          <span>En demos activas <strong>${formatKg(h.demoActivas)} ha</strong></span>
          <span>En demos vencidas <strong>${formatKg(h.demoInactivas)} ha</strong> <span class="db-nota">(no suman al total)</span></span>
        </div>
        ${h.sinTope.length === 0 ? "" : `
        <div class="db-aviso">Planes sin tope asignado: <strong>${clientesSinTope}, ${formatKg(h.haSinTope)} ha en uso</strong> <span class="db-nota">(no suman al total)</span></div>`}`;
    }
    // Sin hectáreas contratadas no hay porcentaje: si igual hay consumidas, todo es exceso.
    const uso = h.contratadas > 0 ? h.consumidas / h.contratadas : h.consumidas > 0 ? Infinity : 0;
    const pct = Number.isFinite(uso) ? Math.round(uso * 100) : null;
    const color = uso >= 1 ? "red" : uso >= UMBRAL_TOPE ? "amber" : "";
    const diferencia = h.contratadas - h.consumidas;
    return `
      <div class="db-ha-big">${formatKg(h.consumidas)} <span class="db-ha-de">de ${formatKg(h.contratadas)} ha</span></div>
      <div class="db-ha-sub">consumidas de las contratadas en planes vigentes · ${pct === null ? "sin hectáreas contratadas" : `${pct}% de uso`}</div>
      <div class="db-bar-track db-bar-track--ha"><div class="db-bar-fill ${color}" style="width:${Math.min(100, pct ?? 100)}%"></div></div>
      <div class="db-cifras">
        ${diferencia >= 0
          ? `<span>Disponibles <strong>${formatKg(diferencia)} ha</strong></span>`
          : `<span class="db-excedidas">Excedidas <strong>${formatKg(-diferencia)} ha</strong></span>`}
        <span>En demo <strong>${formatKg(h.demoActivas)} ha</strong></span>
      </div>
      <div class="db-subt">Clientes en el 90% de su tope o más</div>
      ${h.sinTope.length === 0 && h.cercaDelTope.length === 0 ? `<div class="db-empty">Ninguno.</div>` : `
      <div class="db-lista">
        ${h.sinTope.map((cliente) => `
          <div class="db-row db-row--link" data-cliente-id="${cliente.id}">
            <div class="db-row-main">
              <div class="db-row-name">${this._esc(cliente.nombre)}</div>
              <div class="db-row-sub">${formatKg(cliente.hectareasTotales)} ha · sin tope asignado</div>
            </div>
            <span class="db-tag sin-tope">No asignado</span>
          </div>`).join("")}
        ${h.cercaDelTope.map(({ cliente, uso: usoCliente }) => `
          <div class="db-row db-row--link" data-cliente-id="${cliente.id}">
            <div class="db-row-main">
              <div class="db-row-name">${this._esc(cliente.nombre)}</div>
              <div class="db-row-sub">${formatKg(cliente.hectareasTotales)} de ${formatKg(cliente.maximoHectareasPlataforma)} ha</div>
            </div>
            <span class="db-row-val ${usoCliente >= 1 ? "red" : "amber"}">${Math.round(usoCliente * 100)}%</span>
          </div>`).join("")}
      </div>`}`;
  }

  /** Demos y planes que vencen en los próximos 30 días, del más próximo al más lejano, con «Llamar». */
  private _vencimientosHtml(clientes: ClienteResponse[]): string {
    const lista = clientes.filter((c) => planStatus(c) === "por-vencer").sort((a, b) => msVencimiento(a) - msVencimiento(b));
    if (lista.length === 0) return `<div class="db-empty">Ninguna vence en los próximos 30 días.</div>`;
    return `<div class="db-lista">${lista.map((c) => this._filaLicencia(c)).join("")}</div>`;
  }

  /** Demos y planes ya vencidos, del que venció hace menos al que venció hace más, con «Llamar» para renovar. */
  private _vencidasHtml(clientes: ClienteResponse[]): string {
    const lista = clientes.filter((c) => planStatus(c) === "vencido").sort((a, b) => msVencimiento(b) - msVencimiento(a));
    if (lista.length === 0) return `<div class="db-empty">No hay licencias ni demos vencidas.</div>`;
    return `<div class="db-lista">${lista.map((c) => this._filaLicencia(c)).join("")}</div>`;
  }

  /** Fila de una licencia: demo o plan, el cliente, su fecha y cuánto falta o cuánto pasó, y «Llamar». Abre el cliente. */
  private _filaLicencia(c: ClienteResponse): string {
    return `
      <div class="db-row db-row--link" data-cliente-id="${c.id}">
        <span class="db-tag ${c.isDemoPlataforma ? "demo" : "plan"}">${c.isDemoPlataforma ? "Demo" : "Plan"}</span>
        <div class="db-row-main">
          <div class="db-row-name">${this._esc(c.nombre)}</div>
          <div class="db-row-sub">${fmtVencimiento(c.fechaVencimientoPlataforma!)} · ${textoVence(diasParaVencer(c)!)}</div>
        </div>
        ${this._botonLlamar(c)}
      </div>`;
  }

  /**
   * Agenda de hoy: demos y seguimientos de hoy, en una lista de alto máximo. Cada uno abre su ficha en el Pipeline. Van
   * sin hora: el Pipeline guarda solo la fecha (la registra a las 12:00), así que una hora sería inventada. Los atrasados
   * van en su propio recuadro (`_agendaAtrasadaHtml`).
   */
  private _agendaHtml(a: AgendaResponse): string {
    const html = this._grupoAgenda("Demos de hoy", a.demos, false) + this._grupoAgenda("Seguimientos de hoy", a.pendientes, false);
    return html ? `<div class="db-lista">${html}</div>` : `<div class="db-empty">Nada agendado para hoy.</div>`;
  }

  /**
   * Agenda atrasada: las oportunidades abiertas con el próximo contacto antes de hoy que nadie movió ni cerró, cada una
   * con el día en que tocaba, en rojo, de la más reciente a la más vieja (pedido del usuario: "fecha descendente"; el
   * Server no las ordena). Recuadro aparte desde el 2026-10-29: dentro de «Agenda de hoy» sus fechas confundían
   * (pedido del usuario: "pon Agenda de hoy en una tarjeta y Agenda atrasada en otra").
   */
  private _agendaAtrasadaHtml(a: AgendaResponse): string {
    const vencidos = [...a.vencidos].sort((x, y) => msContacto(y) - msContacto(x));
    const html = this._grupoAgenda("Vencidos", vencidos, true);
    return html ? `<div class="db-lista">${html}</div>` : `<div class="db-empty">Nada atrasado.</div>`;
  }

  /**
   * Un grupo de la agenda: subtítulo con cuántas son y una fila por oportunidad (cliente, etapa y comercial), que abre
   * su ficha en el Pipeline. Nada si la lista está vacía.
   * @param vencidos Las atrasadas muestran el día en que tocaba el contacto, en rojo.
   */
  private _grupoAgenda(titulo: string, lista: OportunidadResponse[], vencidos: boolean): string {
    return lista.length === 0 ? "" : `
      <div class="db-subt">${titulo} (${lista.length})</div>
      ${lista.map((o) => `
        <div class="db-row db-row--link" data-op-id="${o.id}" data-op-cliente="${o.clienteId}">
          <div class="db-row-main">
            <div class="db-row-name">${this._esc(o.clienteNombre)}</div>
            <div class="db-row-sub">${this._esc(getStageLabel(o.stage))}${o.comercialNombre ? ` · ${this._esc(o.comercialNombre)}` : ""}</div>
          </div>
          ${vencidos ? `<span class="db-row-val red">${o.proxContacto ? fmtDia(o.proxContacto) : "—"}</span>` : ""}
        </div>`).join("")}`;
  }

  /**
   * Ortomosaicos: cuántos se están publicando y cuántos fallaron; los fallidos, con la finca y el error, en una lista
   * de alto máximo.
   */
  private _ortomosaicosHtml(items: OrtomosaicoPendienteItem[]): string {
    if (items.length === 0) return `<div class="db-empty">Ninguno en curso ni con error.</div>`;
    const enCurso = items.filter((i) => i.ortomosaicoEstado === OrtomosaicoEstado.PENDIENTE).length;
    const conError = items.filter((i) => i.ortomosaicoEstado === OrtomosaicoEstado.ERROR);
    return `
      <div class="db-mini">
        ${this._mini(formatInt(enCurso), "publicándose", "")}
        ${this._mini(formatInt(conError.length), "con error", "", conError.length > 0 ? "red" : "")}
      </div>
      ${conError.length === 0 ? "" : `
      <div class="db-lista">
        ${conError.map((i) => `
          <div class="db-row">
            <div class="db-row-main">
              <div class="db-row-name">${this._esc(i.nombreFinca)}</div>
              <div class="db-row-sub db-row-sub--error">${this._esc(i.ortomosaicoError ?? "Sin detalle.")}</div>
            </div>
            <span class="db-row-val">${fmtDia(i.ultimaActualizacion)}</span>
          </div>`).join("")}
      </div>`}`;
  }

  /** Campañas: activas, registros de sus landings y demos que activaron. */
  private _campanasHtml(s: CampanaStatsResponse): string {
    return `
      <div class="db-mini">
        ${this._mini(formatInt(s.activas), "activas", `de ${formatInt(s.totalCampanas)}`)}
        ${this._mini(formatInt(s.totalRegistros), "registros", "")}
        ${this._mini(formatInt(s.demosActivadas), "demos activadas", "")}
      </div>`;
  }

  /**
   * Pipeline: los mismos indicadores, embudo, orígenes y comerciales de su vista Dashboard; los comerciales, en una
   * lista de alto máximo.
   */
  private _pipelineHtml(s: PipelineStatsResponse): string {
    const maxEtapa = Math.max(...s.embudo.map((e) => e.count), 1);
    const totalFuentes = s.porFuente.reduce((suma, f) => suma + f.count, 0) || 1;
    return `
      <div class="db-kstrip">
        ${this._kpi(formatInt(s.oportunidadesActivas), "Oportunidades activas", "", "")}
        ${this._kpi(formatInt(s.demosAgendadas), "Demos agendadas", "blue", "")}
        ${this._kpi(formatInt(s.cerradasGanadas), "Ganadas", "green", "")}
        ${this._kpi(formatInt(s.cerradasPerdidas), "Perdidas", "red", "")}
        ${this._kpi(`${s.tasaConversion.toFixed(0)}%`, "Conversión", "", "")}
      </div>
      <div class="db-grid">
        ${this._panelLleno("Embudo por etapa", { texto: "Ver Pipeline", ruta: "/ventas/pipeline" }, s.embudo.length === 0
          ? `<div class="db-empty">Sin datos.</div>`
          : s.embudo.map((e) => this._barra(this._esc(e.label), e.count / maxEtapa, formatInt(e.count), SC[e.stage]?.bar)).join(""))}
        ${this._panelLleno("Oportunidades por origen", undefined, s.porFuente.length === 0
          ? `<div class="db-empty">Sin datos.</div>`
          : s.porFuente.map((f) => `
            <div class="db-row">
              <div class="db-row-main"><div class="db-row-name">${this._esc(SOURCE_LABELS[f.source] ?? f.source)}</div></div>
              <span class="db-row-val">${formatInt(f.count)} · ${Math.round((f.count / totalFuentes) * 100)}%</span>
            </div>`).join(""))}
        ${this._panelLleno("Rendimiento por comercial", undefined, s.rendimientoComerciales.length === 0
          ? `<div class="db-empty">Sin datos.</div>`
          : `<div class="db-lista">${s.rendimientoComerciales.map((r) => `
            <div class="db-row">
              <div class="db-row-main">
                <div class="db-row-name">${this._esc(r.comercial)}</div>
                <div class="db-row-sub">${formatInt(r.leads)} oportunidades · ${formatInt(r.cerrados)} ganada${r.cerrados === 1 ? "" : "s"}</div>
              </div>
              <span class="db-row-val">${r.leads > 0 ? Math.round((r.cerrados / r.leads) * 100) : 0}%</span>
            </div>`).join("")}</div>`)}
      </div>`;
  }

  /**
   * Planes de producción que vencen este mes, ya vencidos o por vencer, por fecha, en una lista de alto máximo. Cada
   * uno abre su cliente.
   */
  private _renovacionesHtml(clientes: ClienteResponse[]): string {
    const hoy = new Date();
    // El vencimiento se guarda a medianoche UTC: el mes se lee en UTC, como lo muestra `fmtVencimiento`.
    const lista = clientes.filter((c) => {
      if (c.isDemoPlataforma || !c.fechaVencimientoPlataforma) return false;
      const fecha = new Date(c.fechaVencimientoPlataforma);
      return fecha.getUTCFullYear() === hoy.getFullYear() && fecha.getUTCMonth() === hoy.getMonth();
    }).sort((a, b) => msVencimiento(a) - msVencimiento(b));
    if (lista.length === 0) return `<div class="db-empty">Ningún plan vence este mes.</div>`;
    return `<div class="db-lista">${lista.map((c) => `
      <div class="db-row db-row--link" data-cliente-id="${c.id}">
        <div class="db-row-main">
          <div class="db-row-name">${this._esc(c.nombre)}</div>
          <div class="db-row-sub">${fmtVencimiento(c.fechaVencimientoPlataforma!)} · ${textoVence(diasParaVencer(c)!)} · ${formatKg(c.maximoHectareasPlataforma)} ha</div>
        </div>
        ${this._botonLlamar(c)}
      </div>`).join("")}</div>`;
  }

  /** Fincas del mapa: total, sin cliente y con más de un dueño (las que cuentan dos veces en las hectáreas de demo). */
  private _fincasHtml(fincas: FincaMapaResponse[]): string {
    const sinCliente = fincas.filter((f) => f.duenos.length === 0).length;
    const compartidas = fincas.filter((f) => f.duenos.length > 1).length;
    return `
      <div class="db-mini">
        ${this._mini(formatInt(fincas.length), "fincas", "")}
        ${this._mini(formatInt(sinCliente), "sin cliente", "", sinCliente > 0 ? "amber" : "")}
        ${this._mini(formatInt(compartidas), "con más de un dueño", "")}
      </div>`;
  }

  /** GPS: total, en clientes, en oficina y sin pasar el testeo. */
  private _gpsHtml(s: GpsTrackerStatsResponse): string {
    return `
      <div class="db-mini">
        ${this._mini(formatInt(s.total), "en total", "")}
        ${this._mini(formatInt(s.asignadosACliente), "en clientes", "")}
        ${this._mini(formatInt(s.enOficina), "en oficina", "")}
        ${this._mini(formatInt(s.noPasanTest), "sin pasar el testeo", "", s.noPasanTest > 0 ? "red" : "")}
      </div>`;
  }

  /** Proyectos: activos y nuevos del mes, hectáreas, y CAPEX y OPEX ejecutado contra el presupuestado. */
  private _proyectosHtml(s: DashboardStatsResponse): string {
    const ejecucion = (titulo: string, ejecutado: number, presupuestado: number): string => {
      const uso = presupuestado > 0 ? ejecutado / presupuestado : 0;
      return this._barra(titulo, uso, `${Math.round(uso * 100)}%`, uso > 1 ? "var(--sx-error)" : undefined)
        + `<div class="db-bar-det">${fmtCentavos(ejecutado)} de ${fmtCentavos(presupuestado)}</div>`;
    };
    return `
      <div class="db-mini">
        ${this._mini(formatInt(s.proyectosActivos), "activos", s.nuevosEsteMes > 0 ? `+${formatInt(s.nuevosEsteMes)} este mes` : "")}
        ${this._mini(formatKg(s.totalHa), "hectáreas", "")}
      </div>
      ${ejecucion("CAPEX", s.capexEjecutado, s.capexPresupuestado)}
      ${ejecucion("OPEX", s.opexEjecutado, s.opexPresupuestado)}`;
  }

  // ── Piezas ──

  /** Recuadro vacío: título, enlace opcional a la pantalla de sus datos y el cuerpo con `id`, que rellena `_cargar`. */
  private _panel(id: string, titulo: string, enlace?: { texto: string; ruta: string }): string {
    return `
      <section class="db-panel">
        <div class="db-panel-hd">
          <h3 class="db-panel-t">${titulo}</h3>
          ${enlace ? `<button class="db-link" data-ruta="${enlace.ruta}">${enlace.texto} →</button>` : ""}
        </div>
        <div id="${id}"></div>
      </section>`;
  }

  /** Recuadro con el contenido ya pintado (los del Pipeline salen de una sola consulta). */
  private _panelLleno(titulo: string, enlace: { texto: string; ruta: string } | undefined, cuerpo: string): string {
    return `
      <section class="db-panel">
        <div class="db-panel-hd">
          <h3 class="db-panel-t">${titulo}</h3>
          ${enlace ? `<button class="db-link" data-ruta="${enlace.ruta}">${enlace.texto} →</button>` : ""}
        </div>
        ${cuerpo}
      </section>`;
  }

  /** Tarjeta de indicador: valor, etiqueta y una línea opcional debajo. */
  private _kpi(valor: string, etiqueta: string, color: string, sub: string): string {
    return `
      <div class="db-kcard">
        <div class="db-k-val ${color}">${valor}</div>
        <div class="db-k-lbl">${etiqueta}</div>
        ${sub ? `<div class="db-k-sub">${sub}</div>` : ""}
      </div>`;
  }

  /** Cifra chica dentro de un recuadro. */
  private _mini(valor: string, etiqueta: string, sub: string, color = ""): string {
    return `
      <div class="db-mini-item">
        <div class="db-mini-val ${color}">${valor}</div>
        <div class="db-mini-lbl">${etiqueta}</div>
        ${sub ? `<div class="db-mini-sub">${sub}</div>` : ""}
      </div>`;
  }

  /** Barra horizontal con etiqueta y valor; `fraccion` de 0 a 1 (lo que pase de 1 se dibuja lleno). */
  private _barra(etiqueta: string, fraccion: number, valor: string, color?: string): string {
    const ancho = Math.round(Math.min(1, Math.max(0, fraccion)) * 100);
    return `
      <div class="db-bar-row">
        <span class="db-bar-lbl">${etiqueta}</span>
        <div class="db-bar-track"><div class="db-bar-fill" style="width:${ancho}%${color ? `;background:${color}` : ""}"></div></div>
        <span class="db-bar-val">${valor}</span>
      </div>`;
  }

  /**
   * Botón «Llamar» del cliente; nada si no tiene teléfono. En celular queda solo el ícono: el CSS esconde el texto y el
   * botón conserva su nombre en `aria-label`. Lo conecta `_conectarClientes`.
   */
  private _botonLlamar(c: ClienteResponse): string {
    return c.telefono
      ? `<button class="sx-btn sx-btn-ghost sx-btn-sm" data-llamar="${c.id}" aria-label="Llamar"><i class="ph ph-phone" aria-hidden="true"></i><span class="db-llamar-txt"> Llamar</span></button>`
      : "";
  }

  /** Las filas con cliente abren su ficha en Clientes, y «Llamar» marca su teléfono, como en la ficha del cliente. */
  private _conectarClientes(el: HTMLElement, clientes: ClienteResponse[]): void {
    el.querySelectorAll<HTMLElement>("[data-cliente-id]").forEach((fila) => {
      fila.onclick = () => {
        ClientesPage.openClienteId = fila.dataset.clienteId!;
        this.navigate("/terceros/clientes");
      };
    });
    el.querySelectorAll<HTMLButtonElement>("[data-llamar]").forEach((boton) => {
      boton.onclick = (e) => {
        e.stopPropagation();
        const cliente = clientes.find((c) => c.id === boton.dataset.llamar);
        if (cliente?.telefono) window.location.href = `tel:${cliente.telefono}`;
      };
    });
  }

  /** Cada fila de la agenda abre la ficha de su oportunidad en el Pipeline. */
  private _conectarAgenda(el: HTMLElement): void {
    el.querySelectorAll<HTMLElement>("[data-op-id]").forEach((fila) => {
      fila.onclick = () => {
        PipelinePage.pendingFicha = { clienteId: fila.dataset.opCliente!, opId: fila.dataset.opId! };
        this.navigate("/ventas/pipeline");
      };
    });
  }

  /** Los enlaces «Ver …» llevan a la pantalla de la que salen los datos del recuadro. */
  private _conectarEnlaces(el: HTMLElement): void {
    el.querySelectorAll<HTMLButtonElement>("[data-ruta]").forEach((boton) => {
      boton.onclick = () => this.navigate(boton.dataset.ruta!);
    });
  }

  /** Elemento por id dentro de la pestaña (existe siempre: lo acaba de pintar la misma pestaña). */
  private _el(vista: HTMLElement, id: string): HTMLElement {
    return vista.querySelector<HTMLElement>(`#${id}`)!;
  }

  /** Escapa el texto que viene de la base antes de meterlo en el HTML. */
  private _esc(texto: string | null | undefined): string {
    return (texto ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  }
}
